/**
 * RUM errors we drop in `beforeSend` because nobody can act on them, and at
 * their volume they would drown any alert on real problems (EN-6505). Each
 * entry says why. Keep this list narrow: a pattern here hides every matching
 * error from Error Tracking for good.
 */
const IGNORED_MESSAGES: RegExp[] = [
  // Link scanners in email clients pre-render our pages and throw this.
  // https://github.com/DataDog/browser-sdk/issues/2715
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // GTM/ad tags blocked or slow to load; our code never calls googletag.
  /googletag is not defined/,
  // A cross-origin script threw. The browser hides the message, stack and
  // file, so there is nothing to debug.
  /^(Uncaught )?"?Script error\.?"?$/,
  // Browser intervention warnings about passive touch listeners, reported
  // through the Reporting API. Not exceptions.
  /Ignored attempt to cancel a touchmove event/,
  /Unable to preventDefault inside passive event listener/,
  // Google Maps script failed to load (network, blockers). The map UI already
  // degrades; the error gives us nothing to fix.
  /<ApiProvider> failed to load the Google Maps JavaScript API/,
  // Monocle script blocked, mostly by ad blockers.
  /MonocleProvider: Script failed to load/,
  // accessiBe widget referenced before (or without) its script loading.
  /acsbJS is not defined/,
  // Next.js redirect() is control flow, thrown on purpose.
  /^NEXT_REDIRECT/,
];

// Network-failure messages from fetch/XHR across browsers. Mostly connection
// drops, but failing calls to our own API must stay visible, so these are only
// dropped when the stack shows a third-party script made the request.
const NETWORK_FAILURE =
  /Failed to fetch|Load failed|NetworkError when attempting to fetch/;

// Third-party scripts whose own requests fail often and are not ours to fix.
// Matched against stack frames; the Datadog SDK's fetch wrapper also appears in
// these stacks, so it is deliberately not listed.
const THIRD_PARTY_SCRIPT_HOSTS = [
  "cdn-cookieyes.com", // CookieYes consent banner
  "acsbapp.com", // accessiBe widget
];

export interface RumErrorFields {
  message: string;
  stack?: string;
  handling_stack?: string;
}

export function isIgnoredRumError(error: RumErrorFields): boolean {
  if (IGNORED_MESSAGES.some((pattern) => pattern.test(error.message))) {
    return true;
  }
  if (NETWORK_FAILURE.test(error.message)) {
    const frames = `${error.stack ?? ""}\n${error.handling_stack ?? ""}`;
    return THIRD_PARTY_SCRIPT_HOSTS.some((host) => frames.includes(host));
  }
  return false;
}
